import { useSettings } from 'connectors/Settings/Settings';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { HreflangLinkApi } from 'graphql/generated';
import { substringStripHtml } from 'helpers/parsing/stripHtml';
import { useSeoPageSettings } from 'hooks/seo/useSeoPageSettings';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { OgTypeEnum } from 'types/seo';

type SeoMetaProps = {
    /** Fallback / default title when no SEO page title from API. When omitted, uses global settings. */
    title?: string | null;
    /** Fallback / default description when no SEO page description from API. When omitted, uses global settings. */
    description?: string | null;
    ogType?: OgTypeEnum | undefined;
    ogImageUrlDefault?: string | null | undefined;
    robotsMetaTag?: string | null;
    defaultHreflangLinks?: HreflangLinkApi[];
};

const SeoMeta: FC<SeoMetaProps> = ({
    title: titleProp,
    description: descriptionProp,
    ogType = OgTypeEnum.Website,
    ogImageUrlDefault,
    robotsMetaTag,
    defaultHreflangLinks,
}) => {
    const settings = useSettings();
    const seoPageSettings = useSeoPageSettings();
    const currentUri = useRouter().asPath;
    const { url, defaultLocaleIdentifier } = useDomainConfig();
    const currentUrlWithDomain = url.substring(0, url.length - 1) + currentUri;

    const titleFromApi = settings?.seo.title;
    const descriptionFromApi = settings?.seo.metaDescription;
    const suffixFromApi = settings?.seo.titleAddOn;

    const pageTitle = substringStripHtml(seoPageSettings.title ?? titleProp ?? titleFromApi + ' ' + suffixFromApi, 70);
    const metaDescription = seoPageSettings.metaDescription ?? descriptionProp ?? descriptionFromApi;
    const pageImageUrl = ogImageUrlDefault ?? url + 'images/tescoma-white-logo.png';

    return (
        <Head>
            <title>{pageTitle}</title>
            {robotsMetaTag && <meta content={robotsMetaTag} name="robots" />}
            {metaDescription && <meta content={substringStripHtml(metaDescription, 160)} name="description" />}

            <meta content="Tescoma e-shop" property="og:site_name" />
            <meta content={defaultLocaleIdentifier.replace('-', '_')} property="og:locale" />
            <meta content={ogType} property="og:type" />
            {pageTitle && <meta content={pageTitle} property="og:title" />}
            {metaDescription && <meta content={substringStripHtml(metaDescription, 160)} property="og:description" />}
            {pageImageUrl && <meta content={pageImageUrl} property="og:image" />}

            <meta content="summary_large_image" property="twitter:card" />
            <meta content={url} property="twitter:domain" />
            <meta content={currentUrlWithDomain} property="twitter:url" />
            {pageTitle && <meta content={pageTitle} property="twitter:title" />}
            {metaDescription && (
                <meta content={substringStripHtml(metaDescription, 160)} property="twitter:description" />
            )}
            {pageImageUrl && <meta content={pageImageUrl} property="twitter:image" />}

            {defaultHreflangLinks?.map(({ hreflang, href }) => (
                <link key={hreflang} href={href} hrefLang={hreflang} rel="alternate" />
            ))}
        </Head>
    );
};

export default SeoMeta;
